import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import { useFieldErrors, type FieldErrors } from '../hooks/useFieldErrors';
import { TimePicker } from '../../../components/common/TimePicker';

interface HoursItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

interface DayRow {
  day_of_week: number;
  is_open: boolean;
  open_time: string;  // 'HH:MM'
  close_time: string; // 'HH:MM'
}

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function normalizeTime(t: string | null | undefined): string {
  if (!t) return '09:00';
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function emptyWeek(): DayRow[] {
  return Array.from({ length: 7 }, (_, i) => ({
    day_of_week: i,
    is_open: false,
    open_time: '09:00',
    close_time: '17:00',
  }));
}

export default function HoursItem({ businessId, onBack, onSaved }: HoursItemProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<DayRow[]>(() => emptyWeek());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from('business_hours')
        .select('day_of_week, is_open, open_time, close_time')
        .eq('business_id', businessId)
        .order('day_of_week');
      if (cancelled) return;
      if (err) {
        setError(`Couldn't load hours: ${err.message}`);
        setLoading(false);
        return;
      }
      const week = emptyWeek();
      for (const row of data ?? []) {
        const idx = row.day_of_week;
        if (idx >= 0 && idx <= 6) {
          week[idx] = {
            day_of_week: idx,
            is_open: !!row.is_open,
            open_time: normalizeTime(row.open_time),
            close_time: normalizeTime(row.close_time),
          };
        }
      }
      setDays(week);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  function updateDay(idx: number, patch: Partial<DayRow>) {
    setDays(prev => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  type DayField = `day-${number}`;
  const validate = (): FieldErrors<DayField> => {
    const e: FieldErrors<DayField> = {};
    for (const d of days) {
      if (d.is_open && d.open_time && d.close_time && d.open_time >= d.close_time) {
        e[`day-${d.day_of_week}` as DayField] = 'Closing time must be after opening.';
      }
    }
    return e;
  };
  const { canSave: formatOk, showError, handleBlur, markAllTouched } =
    useFieldErrors<DayField>(validate);

  const canSave = !saving && formatOk;

  async function handleSave() {
    if (!formatOk) {
      markAllTouched();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const rows = days.map(d => ({
        business_id: businessId,
        day_of_week: d.day_of_week,
        is_open: d.is_open,
        open_time: `${d.open_time}:00`,
        close_time: `${d.close_time}:00`,
      }));
      const { error: e1 } = await supabase
        .from('business_hours')
        .upsert(rows, { onConflict: 'business_id,day_of_week' });
      if (e1) throw e1;
      setSaving(false);
      onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Couldn't save hours: ${message}`);
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Set your hours</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
        }}
      >
        Set your hours
      </h1>
      <p
        style={{
          margin: '8px 0 24px',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: 560,
        }}
      >
        When are you open for business? Clients see this on your booking page, and CrewBit uses it
        to schedule things correctly.
      </p>

      {loading ? (
        <div style={{ fontSize: typography.size.caption, color: admin.caption }}>Loading…</div>
      ) : (
        <>
          <div
            style={{
              background: palette.white,
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              padding: 8,
              marginBottom: 24,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {days.map((d, i) => {
              const fieldKey = `day-${d.day_of_week}` as DayField;
              return (
                <DayRowView
                  key={d.day_of_week}
                  day={d}
                  isLast={i === days.length - 1}
                  error={showError(fieldKey)}
                  onToggle={() => updateDay(i, { is_open: !d.is_open })}
                  onOpenChange={t => updateDay(i, { open_time: t })}
                  onCloseChange={t => updateDay(i, { close_time: t })}
                  onBlur={() => handleBlur(fieldKey)}
                />
              );
            })}
          </div>

          {error && (
            <div
              style={{
                background: palette.redWash,
                border: `1px solid ${palette.red}`,
                borderRadius: radii.md,
                padding: 12,
                marginBottom: 16,
                color: palette.red,
                fontSize: typography.size.caption,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '10px 20px',
                borderRadius: radii.md,
                border: 'none',
                background: canSave ? palette.accent : palette.g200,
                color: palette.white,
                fontSize: typography.size.body,
                fontWeight: typography.weight.semibold,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving…' : 'Save hours'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: radii.md,
                border: 'none',
                background: 'transparent',
                color: admin.caption,
                fontSize: typography.size.body,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DayRowView({
  day,
  isLast,
  error,
  onToggle,
  onOpenChange,
  onCloseChange,
  onBlur,
}: {
  day: DayRow;
  isLast: boolean;
  error: string | null;
  onToggle: () => void;
  onOpenChange: (t: string) => void;
  onCloseChange: (t: string) => void;
  onBlur: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '14px 12px',
        borderBottom: isLast ? 'none' : `1px solid ${palette.g100}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 100,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            color: admin.header,
          }}
        >
          {DAY_LABELS[day.day_of_week]}
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-pressed={day.is_open}
          aria-label={`${DAY_LABELS[day.day_of_week]} ${day.is_open ? 'open' : 'closed'}`}
          style={{
            width: 80,
            padding: '6px 10px',
            borderRadius: radii.full,
            border: `1px solid ${day.is_open ? palette.accent : palette.g300}`,
            background: day.is_open ? palette.accentWash : palette.g50,
            color: day.is_open ? palette.accentDeep : admin.caption,
            fontSize: typography.size.micro,
            fontWeight: typography.weight.semibold,
            cursor: 'pointer',
            fontFamily: 'inherit',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {day.is_open ? 'Open' : 'Closed'}
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 1,
            opacity: day.is_open ? 1 : 0.4,
          }}
        >
          <div style={{ width: 130 }}>
            <TimePicker
              value={day.open_time || null}
              onChange={(v) => { onOpenChange(v ?? '09:00'); onBlur(); }}
              step={15}
              disabled={!day.is_open}
              clearable={false}
              error={error ? ' ' : undefined}
            />
          </div>
          <span style={{ color: admin.caption, fontSize: typography.size.caption }}>to</span>
          <div style={{ width: 130 }}>
            <TimePicker
              value={day.close_time || null}
              onChange={(v) => { onCloseChange(v ?? '17:00'); onBlur(); }}
              step={15}
              disabled={!day.is_open}
              minTime={day.is_open ? (day.open_time || undefined) : undefined}
              clearable={false}
              error={error ? ' ' : undefined}
            />
          </div>
        </div>
      </div>
      {error && (
        <div
          style={{
            fontSize: typography.size.micro,
            color: palette.red,
            fontWeight: typography.weight.medium,
            paddingLeft: 196,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
